import React, {
    useState,
    useEffect,
    useContext,
    useCallback,
    useMemo,
} from 'react'
import { useHistory } from 'react-router-dom'
import { Grid } from '@mui/material'
import {
    AnyAutocomplete,
    AnyInput,
    useValidatedForm,
    AutocompleteOption,
} from '../anyform'
import {
    createTenantUser,
    getTenantUser,
    updateTenantUser,
    getTenantUsersMetadata,
    createUser,
    getUser,
    updateUser,
    getUsersMetadata,
    emptyPromise,
} from '../api'
import {
    FormActions,
    Retrievable,
    Surface,
    UserIcon,
    NewUserIcon,
} from '../components'
import { AppContext } from '../context'
import { FeedbackContext } from '../feedback'
import { translate } from '../i18n'
import { Tenant, User, UserRequest, UserMetadataResponse } from '../model'
import { RouterParams } from '../router'
import { PhoneValidator } from '../validators'

type Data = [User | undefined, UserMetadataResponse]

export default function UserForm({ match }: RouterParams) {
    const history = useHistory()

    const feedbackContext = useContext(FeedbackContext)
    const context = useContext(AppContext)
    const sessionUserId = context.session?.user.id
    const userId = match.params.userId
    const tenantId = context.session?.tenant?.id ?? match.params.tenantId
    const { register, bind, isValid } = useValidatedForm<UserRequest>()
    const [user, setUser] = useState<User>()
    const [tenant, setTenant] = useState<Tenant>()
    const [roleOptions, setRoleOptions] = useState<AutocompleteOption[]>([])

    const validators = useMemo(() => [new PhoneValidator()], [])

    const retrievePromise = useCallback((): Promise<User | undefined> => {
        if (userId) {
            if (tenantId) {
                return getTenantUser(tenantId, userId, true)
            }
            return getUser(userId, true)
        }

        return emptyPromise()
    }, [tenantId, userId])

    const metadataPromise = useCallback((): Promise<UserMetadataResponse> => {
        if (tenantId) {
            return getTenantUsersMetadata(tenantId)
        }
        return getUsersMetadata()
    }, [tenantId])

    const fetchData = useCallback(async (): Promise<Data> => {
        const data = await Promise.all([retrievePromise(), metadataPromise()])

        return data
    }, [retrievePromise, metadataPromise])

    const setData = useCallback(([user, metadata]: Data) => {
        setUser(user)
        setTenant(metadata.tenant)
        setRoleOptions(
            metadata.roles.map((role) => ({
                value: role.id,
                label: translate(`users.roles.${role.id}`) as string,
            }))
        )
    }, [])

    const submitPromise = (): Promise<User> => {
        const request = bind()
        if (userId) {
            if (tenantId) {
                return updateTenantUser(tenantId, userId, request)
            }
            return updateUser(userId, request)
        }

        if (tenantId) {
            return createTenantUser(tenantId, request)
        }
        return createUser(request)
    }

    useEffect(() => {
        if (sessionUserId === userId) {
            history.replace('/profile')
            return
        }
    }, [sessionUserId, userId, history])

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!isValid()) return

        feedbackContext.showBackdrop()
        submitPromise()
            .then((response) => {
                if (match.params.tenantId) {
                    history.push(`/tenants/${match.params.tenantId}/users`)
                } else {
                    history.push('/users')
                }
            })
            .catch((error) => feedbackContext.showError(error.message))
            .finally(feedbackContext.closeBackdrop)
    }

    return (
        <Retrievable
            getPromise={fetchData}
            setData={setData}
            builder={() => (
                <Grid item xs={12}>
                    <Grid container justifyContent="center" alignItems="center">
                        <Grid item xs={12} md={10} lg={7} xl={5}>
                            <Surface
                                title={translate(
                                    userId
                                        ? 'users.edit_user'
                                        : 'users.new_user'
                                )}
                                subtitle={tenant ? tenant.name : undefined}
                                icon={userId ? <UserIcon /> : <NewUserIcon />}
                            >
                                <form
                                    autoComplete="off"
                                    noValidate
                                    onSubmit={onSubmit}
                                >
                                    <Grid
                                        container
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Grid item xs={12}>
                                            <AnyInput
                                                type="text"
                                                name="first_name"
                                                initialValue={user?.first_name}
                                                label={
                                                    translate(
                                                        'users.first_name'
                                                    ) as string
                                                }
                                                required
                                                register={register}
                                            />
                                            <AnyInput
                                                type="text"
                                                name="last_name"
                                                initialValue={user?.last_name}
                                                label={
                                                    translate(
                                                        'users.last_name'
                                                    ) as string
                                                }
                                                required
                                                register={register}
                                            />
                                            <AnyInput
                                                type="email"
                                                name="email"
                                                initialValue={user?.email}
                                                label={
                                                    translate(
                                                        'users.email'
                                                    ) as string
                                                }
                                                required
                                                register={register}
                                            />
                                            <AnyInput
                                                type="text"
                                                name="phone"
                                                initialValue={user?.phone}
                                                label={
                                                    translate(
                                                        'users.phone'
                                                    ) as string
                                                }
                                                validators={validators}
                                                register={register}
                                            />
                                            <AnyAutocomplete
                                                name="role_id"
                                                label={
                                                    translate(
                                                        'users.roles.title'
                                                    ) as string
                                                }
                                                initialValue={user?.role?.id}
                                                options={roleOptions}
                                                register={register}
                                            />
                                        </Grid>
                                        <FormActions id={userId} />
                                    </Grid>
                                </form>
                            </Surface>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        />
    )
}
